import { Link } from "@tanstack/react-router";
import { tools, categories } from "@/lib/tools";
import { ArrowRight } from "lucide-react";

type Props = {
  currentSlug?: string;
  /** Lista explícita de slugs de ferramentas a exibir. Tem prioridade sobre currentSlug. */
  slugs?: string[];
  max?: number;
  heading?: string;
};

export function RelatedTools({ currentSlug, slugs, max = 4, heading }: Props) {
  let related: typeof tools = [];
  let catLabel = "Relacionadas";

  if (slugs && slugs.length) {
    const map = new Map(tools.map((t) => [t.slug, t]));
    related = slugs
      .map((s) => map.get(s))
      .filter((t): t is (typeof tools)[number] => !!t && t.available)
      .slice(0, max);
  } else if (currentSlug) {
    const current = tools.find((t) => t.slug === currentSlug);
    if (!current) return null;
    const sameCat = tools.filter(
      (t) => t.category === current.category && t.slug !== currentSlug && t.available,
    );
    const others = tools.filter(
      (t) => t.category !== current.category && t.slug !== currentSlug && t.available,
    );
    related = [...sameCat, ...others].slice(0, max);
    catLabel = categories.find((c) => c.id === current.category)?.label ?? "Relacionadas";
  }

  if (!related.length) return null;
  const title = heading ?? `Ferramentas relacionadas em ${catLabel}`;

  return (
    <aside aria-labelledby="related-tools-heading" className="mt-4">
      <h2 id="related-tools-heading" className="font-display text-2xl md:text-3xl font-bold mb-5">
        {title}
      </h2>
      <ul className="grid gap-3 sm:grid-cols-2">
        {related.map((t) => (
          <li key={t.slug}>
            <Link
              to="/ferramentas/$slug"
              params={{ slug: t.slug }}
              className="group flex items-start justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft hover:border-primary/40 hover:shadow-md transition"
            >
              <div className="min-w-0">
                <div className="font-display font-bold text-base leading-tight">{t.title}</div>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{t.short}</p>
              </div>
              <ArrowRight className="h-4 w-4 mt-1 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition" />
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-5">
        <Link
          to="/ferramentas"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          Ver todas as ferramentas <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </aside>
  );
}
